import { SegmentationRule } from '@salecycle/experimental/Modal/SegmentationModal';
import { TFunction } from 'i18next';
import moment from 'moment-timezone';
import {
  OffsiteCampaignStatus,
  OffsiteCampaignType,
  SegmentFilter,
  SegmentFilterOptions,
  SegmentFilterType,
  SegmentType,
  VisitorMessageType,
} from 'models';
import { BulkRetargetingCampaign } from 'models/offsite/Campaign/BulkRetargetingCampaign';
import { uuid } from 'utils';
import { CampaignEditorState, PurchaseCheckUnit, TargetLookbackPeriod } from '../state/CampaignEditorState';

export const mapAdditionalSegmentation = (rules: SegmentationRule<any, any, any>[][]): SegmentFilter[][] => {
  return rules.map(AND =>
    AND.map(OR => ({
      ...OR,
      id: uuid(),
      type: SegmentFilterType.Custom,
      value: OR.configuration.value,
      options: OR.type.split('.*.'),
      comparison: OR.configuration.condition || '=', // default value for boolean types
    })),
  );
};

export function mapStateToCampaign(
  siteId: string,
  campaignId: string,
  state: CampaignEditorState,
  t: TFunction,
  isFeatureFlagEnabled = false,
): BulkRetargetingCampaign {
  let startTime: number | undefined;

  if (state.campaign.sendDate) {
    startTime = state.campaign.sendDate.getTime();
  }

  const audience = state.campaign.targetAudience;
  const segments = audience.map(({ audience, days, dateType, startDate, endDate, isUsingDateRange }, index) => {
    const segmentFilters: SegmentFilter[][] = mapAdditionalSegmentation(
      state.campaign.additionalSegmentation[audience],
    );

    let newSegmentFilter: SegmentFilter;
    if (isFeatureFlagEnabled) {
      if (isUsingDateRange) {
        // Convert dates to UTC timestamps to avoid timezone conversion issues
        // Extract the calendar date and create UTC moments to preserve the selected date
        const startDateUTC = moment
          .utc(startDate.format('YYYY-MM-DD'))
          .startOf('day')
          .valueOf();
        const endDateUTC = moment
          .utc(endDate.format('YYYY-MM-DD'))
          .endOf('day')
          .valueOf();
        newSegmentFilter = {
          id: state.campaign.ids?.targetAudience?.[audience] ?? uuid(),
          type: SegmentFilterType.BulkRetargeting,
          value: startDateUTC + '|' + endDateUTC,
          options: [audience],
          comparison: 'between',
        };
      } else {
        newSegmentFilter = {
          id: state.campaign.ids?.targetAudience?.[audience] ?? uuid(),
          type: SegmentFilterType.BulkRetargeting,
          value: String(days),
          options: [audience],
          comparison: '<=',
        };

        if (dateType) {
          newSegmentFilter.value =
            String(days) +
            (dateType === TargetLookbackPeriod.Days ? TargetLookbackPeriod.Days : TargetLookbackPeriod.Months);
        }
      }
    } else {
      // No feature flag, just save days amount
      newSegmentFilter = {
        id: state.campaign.ids?.targetAudience?.[audience] ?? uuid(),
        type: SegmentFilterType.BulkRetargeting,
        value: String(days),
        options: [audience],
        comparison: '<=',
      };
    }

    // push target audience
    segmentFilters[0].push(newSegmentFilter);

    // push purchase check if enabled
    // When feature flag is enabled: check purchaseCheck object, otherwise check enablePurchaseCheck
    const isPurchaseCheckEnabled = isFeatureFlagEnabled
      ? state.campaign.purchaseCheck?.[audience]?.enablePurchaseCheck ?? false
      : state.campaign.enablePurchaseCheck[audience] ?? false;

    if (isPurchaseCheckEnabled) {
      const purchaseFilter: SegmentFilter[] = [];
      const purchaseCheckData = state.campaign.purchaseCheck?.[audience];

      let purchaseCheckValue: string;
      let purchaseCheckComparison: string;

      // Check if using new structure with date range or unit selection (only when feature flag is enabled)
      if (isFeatureFlagEnabled && purchaseCheckData?.isPurchaseCheckUsingDateRange) {
        // Date range mode: format as "startDate|endDate" with 'between' comparison (same as target audience)
        if (purchaseCheckData.purchaseCheckStartDate && purchaseCheckData.purchaseCheckEndDate) {
          // Convert dates to UTC timestamps to avoid timezone conversion issues
          const purchaseCheckStartDateUTC = moment
            .utc(purchaseCheckData.purchaseCheckStartDate.format('YYYY-MM-DD'))
            .startOf('day')
            .valueOf();
          const purchaseCheckEndDateUTC = moment
            .utc(purchaseCheckData.purchaseCheckEndDate.format('YYYY-MM-DD'))
            .endOf('day')
            .valueOf();
          purchaseCheckValue = purchaseCheckStartDateUTC + '|' + purchaseCheckEndDateUTC;
          purchaseCheckComparison = 'between';
        } else {
          // Fallback if dates are missing - use days value, default to 1 if not set
          const fallbackDays = purchaseCheckData?.purchaseCheckDays ?? state.campaign.purchaseCheckDays[audience] ?? 1;
          purchaseCheckValue = fallbackDays + '';
          purchaseCheckComparison = '>';
        }
      } else if (isFeatureFlagEnabled && purchaseCheckData) {
        // Days/months mode: use appropriate value based on unit and append suffix (only when feature flag is enabled)
        if (purchaseCheckData.purchaseCheckUnit === PurchaseCheckUnit.Months) {
          purchaseCheckValue = purchaseCheckData.purchaseCheckMonths + 'M';
        } else {
          purchaseCheckValue = purchaseCheckData.purchaseCheckDays + 'd';
        }
        purchaseCheckComparison = '>';
      } else {
        // Fallback to old structure (when feature flag is disabled) - no suffix
        purchaseCheckValue = state.campaign.purchaseCheckDays[audience] + '';
        purchaseCheckComparison = '>';
      }

      purchaseFilter.push({
        id: state.campaign.ids?.purchaseCheckId?.[audience] ?? uuid(),
        type: SegmentFilterType.BulkRetargeting,
        value: purchaseCheckValue,
        options: [SegmentFilterOptions.PurchaseCheck],
        comparison: purchaseCheckComparison,
      });

      segmentFilters.push(purchaseFilter);
    }

    const subjectLine = state.campaign.subjectLine[audience];
    const template = state.campaign.template[audience];

    return {
      id: state.campaign.ids?.segmentId?.[audience] ?? uuid(),
      name: state.campaign.name + ' - Segment ' + index,
      type: SegmentType.All,
      segmentFilters,
      percentageSplits: [
        {
          id: state.campaign.ids?.percentageSplitTestId?.[audience] ?? uuid(),
          isDefault: true, // At least one is required to be default
          name: state.campaign.name + ' - Percentage Split ' + index,
          percentage: 100,
          cycles: [
            {
              id: state.campaign.ids?.cycleId?.[audience] ?? uuid(),
              name: state.campaign.name + ' - Cycle ' + index,
              messageType: VisitorMessageType.Email,
              delay: 0,
              subjectLineId: subjectLine?.id,
              templateId: template?.id,
            },
          ],
        },
      ],
    };
  });

  return {
    name: state.campaign.name,
    type: OffsiteCampaignType.BulkRetargting,
    segments,
    startTime,
    // Following fields are required in order to pass the
    // PUT validation.
    version: 'v2',
    siteId,
    id: campaignId,
    lastEditingComment: 'Save and continue',
    state: OffsiteCampaignStatus.Draft,
    bulkSortByFeature: state.campaign.bulkSortByFeature,
  };
}
